import type { InputHTMLAttributes, ReactNode } from 'react';
import { useState, forwardRef, useId } from 'react';
import './TextInput.css';

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * ReactNode rather than string so a caller can de-emphasise part of it — an "(optional)" suffix,
   * say — without hand-rolling the label and losing the primitive's own typography.
   */
  label?: ReactNode;
  error?: string;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(({
  label,
  error,
  type = 'text',
  className = '',
  id: idProp,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword && showPassword ? 'text' : type;

  // Falls back to a generated id so every caller gets a label association,
  // not just the ones that remembered to pass one.
  const generatedId = useId();
  const inputId = idProp ?? generatedId;
  const errorId = `${inputId}-error`;

  return (
    <div className={`text-input ${className}`}>
      {label && (
        <label className="text-input__label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <div className={`text-input__wrapper ${error ? 'text-input__wrapper--error' : ''}`}>
        <input
          ref={ref}
          id={inputId}
          className="text-input__field"
          type={inputType}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          {...props}
        />
        {isPassword && (
          <button
            type="button"
            className="text-input__toggle"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 17 17"
              fill="none"
              aria-hidden="true"
              focusable="false"
              xmlns="http://www.w3.org/2000/svg"
            >
              {showPassword ? (
                // Eye icon (visible)
                <>
                  <path
                    d="M8.5 3.5C4.5 3.5 1.5 8.5 1.5 8.5C1.5 8.5 4.5 13.5 8.5 13.5C12.5 13.5 15.5 8.5 15.5 8.5C15.5 8.5 12.5 3.5 8.5 3.5Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="8.5"
                    cy="8.5"
                    r="2.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </>
              ) : (
                // Eye-slash icon (hidden)
                <>
                  <path
                    d="M2.5 2.5L14.5 14.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M8.5 5C10.5 5 12.5 6.5 13.5 8.5C13 9.5 12 11 10.5 12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6.5 12C5 11 4 9.5 3.5 8.5C4.5 6.5 6.5 5 8.5 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </>
              )}
            </svg>
          </button>
        )}
      </div>
      {error && (
        <span className="text-input__error" id={errorId}>{error}</span>
      )}
    </div>
  );
});

TextInput.displayName = 'TextInput';
